import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Link } from "react-router-dom";
import "primereact/resources/themes/lara-light-blue/theme.css";
import { Toast } from "primereact/toast";
import { toastify } from "../../../../utils/toast";
import { ConfirmDialog } from "primereact/confirmdialog";

import Layout from "../../component/layout";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { useFacilityStore } from "../../../../app/z-store/store";
import DutyRosterCalendar from "./DutyRosterCalendar";

// Separate Modal Components
import MasterWorkplanModal from "./modals/MasterWorkplanModal";
import ChildWorkplanModal from "./modals/ChildWorkplanModal";
import DutyRosterModal from "./modals/DutyRosterModal";
import TaskCompletionModal from "./modals/TaskCompletionModal";
import ChecklistManagementModal from "./modals/ChecklistManagementModal";
import DailyChecklistTab from "./tabs/dailyChecklistTab";

const WorkplanManagement = () => {
  // State Management
  const [masterWorkplans, setMasterWorkplans] = useState([]);
  const [childWorkplans, setChildWorkplans] = useState([]);
  const [dutyRoster, setDutyRoster] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [taskCompletions, setTaskCompletions] = useState([]); // Daily task completions
  const [checklists, setChecklists] = useState([]); // Duty roster checklists
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [activeTab, setActiveTab] = useState("master-workplan");
  const [expandedRows, setExpandedRows] = useState(null); // For DataTable row expansion

  // Modal States
  const [showMasterWorkplanModal, setShowMasterWorkplanModal] = useState(false);
  const [showChildWorkplanModal, setShowChildWorkplanModal] = useState(false);
  const [showDutyRosterModal, setShowDutyRosterModal] = useState(false);
  const [showTaskCompletionModal, setShowTaskCompletionModal] = useState(false);
  const [showChecklistModal, setShowChecklistModal] = useState(false);

  // Selected Items
  const [selectedMasterWorkplan, setSelectedMasterWorkplan] = useState(null);
  const [selectedChildWorkplan, setSelectedChildWorkplan] = useState(null);
  const [selectedDutyRoster, setSelectedDutyRoster] = useState(null);
  const [selectedMasterForChild, setSelectedMasterForChild] = useState(null);
  const [selectedTaskForCompletion, setSelectedTaskForCompletion] =
    useState(null);
  const [selectedRosterForChecklist, setSelectedRosterForChecklist] =
    useState(null);

  const toast = useRef(null);
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  useEffect(() => {
    if (selectedFacilityId) {
      fetchMasterWorkplans();
      fetchChildWorkplans();
      fetchDutyRoster();
      fetchStaffUsers();
      fetchTaskCompletions();
      fetchChecklists();
    }
  }, [selectedFacilityId]);

  // API Calls
  const fetchMasterWorkplans = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `/api/work_plan/master/${selectedFacilityId}`,
        "GET",
      );

      if (
        response &&
        response.success &&
        response.data &&
        response.data.masterWorkplans
      ) {
        setMasterWorkplans(
          Array.isArray(response.data.masterWorkplans)
            ? response.data.masterWorkplans
            : [],
        );
      } else if (response && response.masterWorkplans) {
        setMasterWorkplans(
          Array.isArray(response.masterWorkplans)
            ? response.masterWorkplans
            : [],
        );
      } else {
        console.log("Unexpected response structure:", response);
        setMasterWorkplans([]);
        toastify("Failed to fetch master workplans", "error");
      }
    } catch (error) {
      console.error("Error fetching master workplans:", error);
      toastify("Error fetching master workplans", "error");
      setMasterWorkplans([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchChildWorkplans = async () => {
    try {
      const response = await makeRequest2(
        `/api/work_plan/child/${selectedFacilityId}`,
        "GET",
      );

      let childWorkplans = [];

      if (response && response.success && response.data) {
        if (response.data.data && response.data.data.childWorkplans) {
          childWorkplans = response.data.data.childWorkplans;
        } else if (response.data.childWorkplans) {
          childWorkplans = response.data.childWorkplans;
        }
      } else if (response && response.childWorkplans) {
        childWorkplans = response.childWorkplans;
      }

      if (Array.isArray(childWorkplans)) {
        setChildWorkplans(childWorkplans);
      } else {
        console.log("Child workplans is not an array:", childWorkplans);
        setChildWorkplans([]);
        toastify("Failed to fetch child workplans", "error");
      }
    } catch (error) {
      console.error("Error fetching child workplans:", error);
      toastify("Error fetching child workplans", "error");
      setChildWorkplans([]);
    }
  };

  const fetchDutyRoster = async () => {
    try {
      const response = await makeRequest2(
        `/api/duty_roster/${selectedFacilityId}?includeChecklist=true`,
        "GET",
      );

      if (response.success) {
        const rosterData =
          response.data?.entries ||
          response.data?.dutyRoster ||
          response.data ||
          [];
        setDutyRoster(Array.isArray(rosterData) ? rosterData : []);
      } else {
        toastify("Failed to fetch duty roster entries", "error");
      }
    } catch (error) {
      toastify("Error fetching duty roster", "error");
    }
  };

  const fetchStaffUsers = async () => {
    try {
      const response = await makeRequest2(
        `/api/universal/users/get/staff`,
        "GET",
      );

      if (response.success) {
        const users = response.data?.users || response.data || [];
        setStaffUsers(Array.isArray(users) ? users : []);
      } else {
        toastify("Failed to fetch staff users", "error");
      }
    } catch (error) {
      toastify("Error fetching staff users", "error");
    }
  };

  const fetchTaskCompletions = async () => {
    try {
      const response = await makeRequest2(
        `/api/work_plan/task_completions/${selectedFacilityId}`,
        "GET",
      );

      if (response && response.success && response.data) {
        setTaskCompletions(response.data.completions || []);
      }
    } catch (error) {
      console.log("Task completions endpoint not available yet");
      setTaskCompletions([]);
    }
  };

  // NEW: Fetch duty roster checklists
  const fetchChecklists = async () => {
    try {
      const response = await makeRequest2(
        `/api/duty_roster_checklist/${selectedFacilityId}`,
        "GET",
      );

      if (response && response.success && response.data) {
        setChecklists(response.data.checklists || []);
      }
    } catch (error) {
      console.log("Checklists endpoint not available yet");
      setChecklists([]);
    }
  };

  // Modal Handlers
  const handleMasterWorkplanModalOpen = (workplan = null) => {
    setSelectedMasterWorkplan(workplan);
    setShowMasterWorkplanModal(true);
  };

  const handleChildWorkplanModalOpen = (
    workplan = null,
    masterWorkplanId = null,
  ) => {
    setSelectedChildWorkplan(workplan);
    setSelectedMasterForChild(masterWorkplanId);
    setShowChildWorkplanModal(true);
  };

  const handleDutyRosterModalOpen = (roster = null) => {
    setSelectedDutyRoster(roster);
    setShowDutyRosterModal(true);
  };

  // NEW: Checklist modal handler
  const handleChecklistModalOpen = (roster) => {
    setSelectedRosterForChecklist(roster);
    setShowChecklistModal(true);
  };

  // Success Handlers
  const handleMasterWorkplanSuccess = () => {
    fetchMasterWorkplans();
    setShowMasterWorkplanModal(false);
    toastify(
      selectedMasterWorkplan
        ? "Master workplan updated successfully"
        : "Master workplan created successfully",
      "success",
    );
  };

  const handleChildWorkplanSuccess = () => {
    fetchChildWorkplans();
    setShowChildWorkplanModal(false);
    toastify(
      selectedChildWorkplan
        ? "Child workplan updated successfully"
        : "Child workplan created successfully",
      "success",
    );
  };

  // Enhanced duty roster success handler
  const handleDutyRosterSuccess = () => {
    fetchDutyRoster();
    // Wait a moment for the duty roster to be saved, then fetch checklists
    setTimeout(() => {
      fetchChecklists();
    }, 1000);
    setShowDutyRosterModal(false);
    toastify(
      selectedDutyRoster
        ? "Duty roster updated successfully"
        : "Duty roster created successfully",
      "success",
    );
  };

  // NEW: Checklist success handler
  const handleChecklistSuccess = () => {
    fetchChecklists();
    fetchDutyRoster(); // Refresh roster data to get updated summaries
    setShowChecklistModal(false);
    toastify("Checklist updated successfully", "success");
  };

  // Delete Child Workplan
  const handleDeleteChildWorkplan = async (childId) => {
    if (window.confirm("Are you sure you want to delete this child task?")) {
      try {
        const response = await makeRequest2(
          `/api/work_plan/child/${selectedFacilityId}/${childId}`,
          "DELETE",
        );

        if (response && (response.success || response.message)) {
          toastify("Child task deleted successfully", "success");
          fetchChildWorkplans();
        } else {
          toastify("Failed to delete child task", "error");
        }
      } catch (error) {
        toastify("Error deleting child task", "error");
      }
    }
  };

  // Task Completion Functions
  const handleMarkTaskCompletion = (childTask) => {
    setSelectedTaskForCompletion(childTask);
    setShowTaskCompletionModal(true);
  };

  const submitTaskCompletion = async (completionData) => {
    try {
      const response = await makeRequest2(
        `/api/work_plan/task_completions/${selectedFacilityId}`,
        "POST",
        {
          childWorkplanId: selectedTaskForCompletion._id,
          masterWorkplanId: selectedTaskForCompletion.parent,
          completedDate: completionData.date,
          completedBy: completionData.staffId,
          notes: completionData.notes,
          status: completionData.status || "completed",
        },
      );

      if (response && response.success) {
        toastify("Task completion recorded successfully", "success");
        fetchTaskCompletions();
        setShowTaskCompletionModal(false);
      } else {
        toastify("Failed to record task completion", "error");
      }
    } catch (error) {
      toastify("Error recording task completion", "error");
    }
  };

  // Check if a task is completed for a specific date
  const isTaskCompletedOnDate = (childTaskId, date) => {
    const dateStr = new Date(date).toISOString().split("T")[0];
    return taskCompletions.some(
      (completion) =>
        completion.childWorkplanId === childTaskId &&
        new Date(completion.completedDate).toISOString().split("T")[0] ===
          dateStr &&
        completion.status === "completed",
    );
  };

  // Get completion details for a task on a date
  const getTaskCompletionDetails = (childTaskId, date) => {
    const dateStr = new Date(date).toISOString().split("T")[0];
    return taskCompletions.find(
      (completion) =>
        completion.childWorkplanId === childTaskId &&
        new Date(completion.completedDate).toISOString().split("T")[0] ===
          dateStr,
    );
  };

  // Filter Functions
  const filteredMasterWorkplans = masterWorkplans.filter((workplan) =>
    Object.values(workplan).some((value) =>
      value?.toString().toLowerCase().includes(searchKeyword.toLowerCase()),
    ),
  );

  const filteredChildWorkplans = childWorkplans.filter((workplan) =>
    Object.values(workplan).some((value) =>
      value?.toString().toLowerCase().includes(searchKeyword.toLowerCase()),
    ),
  );

  const filteredDutyRoster = dutyRoster.filter((roster) =>
    Object.values(roster).some((value) =>
      value?.toString().toLowerCase().includes(searchKeyword.toLowerCase()),
    ),
  );

  // Action Templates
  const masterWorkplanActionTemplate = (rowData) => (
    <div className="d-flex gap-2">
      <Button
        icon="ti ti-edit"
        className="p-button-rounded p-button-info p-button-sm"
        onClick={() => handleMasterWorkplanModalOpen(rowData)}
        tooltip="Edit"
      />
      <Button
        icon="ti ti-plus"
        className="p-button-rounded p-button-success p-button-sm"
        onClick={() => handleChildWorkplanModalOpen(null, rowData._id)}
        tooltip="Add Child Workplan"
      />
    </div>
  );

  const childWorkplanActionTemplate = (rowData) => (
    <div className="d-flex gap-2">
      <Button
        icon="ti ti-edit"
        className="p-button-rounded p-button-info p-button-sm"
        onClick={() => handleChildWorkplanModalOpen(rowData)}
        tooltip="Edit"
      />
    </div>
  );

  // Enhanced duty roster action template with checklist management
  const dutyRosterActionTemplate = (rowData) => (
    <div className="d-flex gap-2">
      <Button
        icon="ti ti-edit"
        className="p-button-rounded p-button-info p-button-sm"
        onClick={() => handleDutyRosterModalOpen(rowData)}
        tooltip="Edit Roster"
      />
      <Button
        icon="ti ti-checklist"
        className="p-button-rounded p-button-success p-button-sm"
        onClick={() => handleChecklistModalOpen(rowData)}
        tooltip="Manage Checklist"
      />
    </div>
  );

  // Status Templates
  const statusTemplate = (rowData) => {
    const statusColors = {
      Approved: "success",
      Rejected: "danger",
      Pending: "warning",
      active: "success",
      inactive: "secondary",
      completed: "info",
    };

    const status = rowData.status || "Pending";
    return (
      <span className={`badge bg-${statusColors[status] || "secondary"}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Life Status Template
  const lifeStatusTemplate = (rowData) => {
    const lifeColors = {
      Active: "success",
      Inactive: "secondary",
    };

    const life = rowData.life || "Active";
    return (
      <span className={`badge bg-${lifeColors[life] || "secondary"}`}>
        {life}
      </span>
    );
  };

  // NEW: Checklist status template
  const checklistStatusTemplate = (rowData) => {
    if (!rowData.checklistSummary) {
      return <span className="badge bg-secondary">No Checklist</span>;
    }

    const { completionPercentage, status } = rowData.checklistSummary;
    let badgeClass = "bg-secondary";
    let text = "No Data";

    if (status === "active") {
      if (completionPercentage >= 80) {
        badgeClass = "bg-success";
        text = `${completionPercentage}% Complete`;
      } else if (completionPercentage >= 50) {
        badgeClass = "bg-warning";
        text = `${completionPercentage}% Complete`;
      } else {
        badgeClass = "bg-danger";
        text = `${completionPercentage}% Complete`;
      }
    } else {
      badgeClass = "bg-info";
      text = status.charAt(0).toUpperCase() + status.slice(1);
    }

    return <span className={`badge ${badgeClass}`}>{text}</span>;
  };

  // NEW: Today's tasks template
  const todaysTasksTemplate = (rowData) => {
    if (!rowData.checklistSummary) {
      return <span className="text-muted">-</span>;
    }

    const count = rowData.checklistSummary.todaysTasksCount || 0;
    return (
      <span className={`badge ${count > 0 ? "bg-primary" : "bg-secondary"}`}>
        {count} {count === 1 ? "task" : "tasks"}
      </span>
    );
  };

  // Enhanced duty roster expansion template with checklist data
  const dutyRosterExpansionTemplate = (data) => {
    // Find checklist for this duty roster
    const checklist = checklists.find((c) => c.dutyRosterId === data._id);

    return (
      <div className="p-3">
        <div className="row">
          {/* Basic Roster Info */}
          <div className="col-md-6">
            <h6 className="mb-3">Roster Details</h6>
            <div className="table-responsive">
              <table className="table table-sm">
                <tbody>
                  <tr>
                    <td>
                      <strong>Staff:</strong>
                    </td>
                    <td>{data.staffDetails?.fullName || "N/A"}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Position:</strong>
                    </td>
                    <td>{data.staffPosition}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Master Workplan:</strong>
                    </td>
                    <td>{data.masterWorkplanDetails?.title || "N/A"}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Period:</strong>
                    </td>
                    <td>
                      {new Date(data.startDate).toLocaleDateString()} -{" "}
                      {new Date(data.endDate).toLocaleDateString()}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Status:</strong>
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          data.calculatedStatus === "active"
                            ? "bg-success"
                            : data.calculatedStatus === "upcoming"
                              ? "bg-warning"
                              : "bg-secondary"
                        }`}
                      >
                        {data.calculatedStatus}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Checklist Summary */}
          <div className="col-md-6">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">Checklist Summary</h6>
              <Button
                icon="ti ti-checklist"
                label="Manage Checklist"
                className="p-button-sm p-button-success"
                onClick={() => handleChecklistModalOpen(data)}
              />
            </div>

            {checklist ? (
              <div>
                <div className="row text-center mb-3">
                  <div className="col-3">
                    <div className="card bg-light">
                      <div className="card-body p-2">
                        <h6 className="mb-0 text-success">
                          {checklist.summary?.completedTasks || 0}
                        </h6>
                        <small>Completed</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="card bg-light">
                      <div className="card-body p-2">
                        <h6 className="mb-0 text-warning">
                          {checklist.summary?.pendingTasks || 0}
                        </h6>
                        <small>Pending</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="card bg-light">
                      <div className="card-body p-2">
                        <h6 className="mb-0 text-danger">
                          {checklist.summary?.missedTasks || 0}
                        </h6>
                        <small>Missed</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="card bg-light">
                      <div className="card-body p-2">
                        <h6 className="mb-0 text-primary">
                          {checklist.todaysTasksCount || 0}
                        </h6>
                        <small>Today</small>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="progress mb-2" style={{ height: "20px" }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{
                      width: `${checklist.summary?.completionPercentage || 0}%`,
                    }}
                    aria-valuenow={checklist.summary?.completionPercentage || 0}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  >
                    {checklist.summary?.completionPercentage || 0}%
                  </div>
                </div>
                <small className="text-muted">
                  Overall Progress: {checklist.summary?.completedTasks || 0} of{" "}
                  {checklist.summary?.totalTasks || 0} tasks completed
                </small>
              </div>
            ) : (
              <div className="text-center py-3">
                <i
                  className="ti ti-clipboard-off"
                  style={{ fontSize: "32px", color: "#ccc" }}
                ></i>
                <p className="mt-2 text-muted">
                  {data.masterWorkplanDetails
                    ? "Checklist not found - may need to be created"
                    : "No master workplan assigned"}
                </p>
                <Button
                  icon="ti ti-plus"
                  label="Create Checklist"
                  className="p-button-sm p-button-outlined"
                  onClick={() => handleCreateChecklist(data)}
                  disabled={!data.masterWorkplanDetails}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // NEW: Function to create checklist for existing duty roster
  const handleCreateChecklist = async (roster) => {
    try {
      setLoading(true);

      // Check if master workplan has child workplans
      const masterWorkplanChildren = childWorkplans.filter((child) => {
        if (typeof child.parent === "object" && child.parent !== null) {
          return child.parent._id === roster.masterWorkplanId;
        } else if (typeof child.parent === "string") {
          return child.parent === roster.masterWorkplanId;
        }
        return false;
      });

      if (masterWorkplanChildren.length === 0) {
        toastify(
          "Cannot create checklist: No child tasks found for this master workplan",
          "error",
        );
        return;
      }

      // Create checklist by updating the duty roster with checklist generation options
      const response = await makeRequest2(
        `/api/duty_roster/${selectedFacilityId}/${roster._id}`,
        "PUT",
        {
          ...roster,
          checklistOptions: {
            generateChecklist: true,
            taskFrequency: "daily",
          },
        },
      );

      if (response && response.success) {
        toastify("Checklist created successfully", "success");
        fetchChecklists();
        fetchDutyRoster();
      } else {
        toastify("Failed to create checklist", "error");
      }
    } catch (error) {
      console.error("Error creating checklist:", error);
      toastify("Error creating checklist", "error");
    } finally {
      setLoading(false);
    }
  };

  // SIMPLIFIED Master Workplan Expansion Template (removed daily completion tracking)
  const masterWorkplanExpansionTemplate = (data) => {
    const masterWorkplanChildren = childWorkplans.filter((child) => {
      if (typeof child.parent === "object" && child.parent !== null) {
        return child.parent._id === data._id;
      } else if (typeof child.parent === "string") {
        return child.parent === data._id;
      }
      return false;
    });

    return (
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">
            Child Tasks for "{data.title}" ({masterWorkplanChildren.length}{" "}
            tasks)
          </h6>
          <Button
            icon="ti ti-plus"
            label="Add Child Task"
            className="p-button-sm p-button-success"
            onClick={() => handleChildWorkplanModalOpen(null, data._id)}
          />
        </div>

        {masterWorkplanChildren.length === 0 ? (
          <div className="text-center py-3">
            <i
              className="ti ti-list-details"
              style={{ fontSize: "32px", color: "#ccc" }}
            ></i>
            <p className="mt-2 text-muted">
              No child tasks found. Add the first task to get started.
            </p>
            <Button
              icon="ti ti-plus"
              label="Add First Task"
              className="p-button-sm p-button-primary"
              onClick={() => handleChildWorkplanModalOpen(null, data._id)}
            />
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-striped">
              <thead>
                <tr>
                  <th>Task Name</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {masterWorkplanChildren.map((child) => (
                  <tr key={child._id}>
                    <td>
                      <strong>{child.name}</strong>
                    </td>
                    <td>
                      <span title={child.description}>
                        {child.description?.length > 50
                          ? `${child.description.substring(0, 50)}...`
                          : child.description || "No description"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          child.status === "Completed"
                            ? "bg-success"
                            : child.status === "In Progress"
                              ? "bg-primary"
                              : child.status === "Pending"
                                ? "bg-warning"
                                : "bg-secondary"
                        }`}
                      >
                        {child.status}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          icon="ti ti-edit"
                          className="p-button-rounded p-button-info p-button-sm"
                          onClick={() => handleChildWorkplanModalOpen(child)}
                          tooltip="Edit Task"
                        />
                        <Button
                          icon="ti ti-trash"
                          className="p-button-rounded p-button-danger p-button-sm"
                          onClick={() => handleDeleteChildWorkplan(child._id)}
                          tooltip="Delete Task"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-3">
          <div className="alert alert-info">
            <small>
              <i className="ti ti-info-circle me-1"></i>
              <strong>Note:</strong> Daily task completion tracking is available
              in the Duty Roster checklists. Assign these tasks to staff through
              duty rosters to enable completion tracking.
            </small>
          </div>
        </div>
      </div>
    );
  };

  // Template functions for data display
  const masterWorkplanTemplate = (rowData) => {
    if (rowData.parent && typeof rowData.parent === "object") {
      return rowData.parent.title || "N/A";
    } else if (rowData.parent) {
      const masterWorkplan = masterWorkplans.find(
        (m) => m._id === rowData.parent,
      );
      return masterWorkplan ? masterWorkplan.title : "N/A";
    } else if (rowData.masterWorkplanId) {
      const masterWorkplan = masterWorkplans.find(
        (m) => m._id === rowData.masterWorkplanId,
      );
      return masterWorkplan ? masterWorkplan.title : "N/A";
    }
    return "N/A";
  };

  const staffTemplate = (rowData) => {
    if (rowData.staffDetails && rowData.staffDetails.fullName) {
      return rowData.staffDetails.fullName;
    } else if (
      rowData.staffId &&
      typeof rowData.staffId === "object" &&
      rowData.staffId.fullName
    ) {
      return rowData.staffId.fullName;
    } else if (
      rowData.staffId &&
      typeof rowData.staffId === "object" &&
      rowData.staffId.name
    ) {
      return rowData.staffId.name;
    }
    return "N/A";
  };

  // Master Workplan Template for Duty Roster
  const dutyRosterMasterWorkplanTemplate = (rowData) => {
    if (rowData.masterWorkplanDetails && rowData.masterWorkplanDetails.title) {
      return rowData.masterWorkplanDetails.title;
    } else if (
      rowData.masterWorkplanId &&
      typeof rowData.masterWorkplanId === "object" &&
      rowData.masterWorkplanId.title
    ) {
      return rowData.masterWorkplanId.title;
    } else if (rowData.masterWorkplanId) {
      const masterWorkplan = masterWorkplans.find(
        (m) => m._id === rowData.masterWorkplanId,
      );
      return masterWorkplan ? masterWorkplan.title : "N/A";
    }
    return "N/A";
  };

  // Render Header
  const renderHeader = (title, buttonText, onButtonClick) => (
    <div className="d-flex justify-content-between align-items-center">
      <input
        type="text"
        className="form-control w-25"
        placeholder={`Search ${title.toLowerCase()}...`}
        value={searchKeyword}
        onChange={(e) => setSearchKeyword(e.target.value)}
      />
      <button className="btn btn-primary" onClick={onButtonClick}>
        {buttonText}
      </button>
    </div>
  );

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to={"/facility/"}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={""}>Workplan & Duty Management</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <div className="card">
                <div className="card-body py-0">
                  <ul
                    className="nav nav-tabs profile-tabs"
                    id="myTab"
                    role="tablist"
                  >
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === "master-workplan" ? "active" : ""}`}
                        onClick={() => setActiveTab("master-workplan")}
                        href="#master-workplan"
                      >
                        <i className="ti ti-clipboard me-2"></i>
                        Master Workplans
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === "duty-roster" ? "active" : ""}`}
                        onClick={() => setActiveTab("duty-roster")}
                        href="#duty-roster"
                      >
                        <i className="ti ti-calendar me-2"></i>
                        Duty Roster
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === "task-checklists" ? "active" : ""}`}
                        onClick={() => setActiveTab("task-checklists")}
                        href="#task-checklists"
                      >
                        <i className="ti ti-checklist me-2"></i>
                        Task Checklists
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === "daily-checklist" ? "active" : ""}`}
                        onClick={() => setActiveTab("daily-checklist")}
                        href="#daily-checklist"
                      >
                        <i className="ti ti-clipboard-check me-2"></i>
                        Daily Checklist
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === "calendar" ? "active" : ""}`}
                        onClick={() => setActiveTab("calendar")}
                        href="#calendar"
                      >
                        <i className="ti ti-calendar-event me-2"></i>
                        Calendar View
                      </a>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="tab-content">
                {/* Master Workplans Tab with Child Workplans Accordion */}
                <div
                  className={`tab-pane ${activeTab === "master-workplan" ? "show active" : ""}`}
                  id="master-workplan"
                >
                  <div className="card">
                    <div className="card-body">
                      <DataTable
                        value={filteredMasterWorkplans}
                        emptyMessage="No master workplans found."
                        sortMode="multiple"
                        paginator
                        rows={10}
                        stripedRows
                        loading={loading}
                        header={renderHeader(
                          "Master Workplans",
                          "Add Master Workplan",
                          () => handleMasterWorkplanModalOpen(),
                        )}
                        tableStyle={{ minWidth: "50rem" }}
                        expandedRows={expandedRows}
                        onRowToggle={(e) => setExpandedRows(e.data)}
                        rowExpansionTemplate={masterWorkplanExpansionTemplate}
                      >
                        <Column expander style={{ width: "3em" }} />
                        <Column field="title" header="Title" sortable />
                        <Column
                          field="description"
                          header="Description"
                          sortable
                        />
                        <Column
                          field="status"
                          header="Status"
                          body={statusTemplate}
                          sortable
                        />
                        <Column
                          field="life"
                          header="Life Status"
                          body={lifeStatusTemplate}
                          sortable
                        />
                        <Column
                          field="childCount"
                          header="Child Tasks"
                          body={(rowData) => {
                            const childCount = childWorkplans.filter(
                              (child) => {
                                if (
                                  typeof child.parent === "object" &&
                                  child.parent !== null
                                ) {
                                  return child.parent._id === rowData._id;
                                } else if (typeof child.parent === "string") {
                                  return child.parent === rowData._id;
                                }
                                return false;
                              },
                            ).length;

                            return (
                              <span className="badge bg-primary">
                                {childCount}{" "}
                                {childCount === 1 ? "task" : "tasks"}
                              </span>
                            );
                          }}
                          sortable
                        />
                        <Column
                          body={masterWorkplanActionTemplate}
                          header="Actions"
                        />
                      </DataTable>
                    </div>
                  </div>
                </div>

                {/* Enhanced Duty Roster Tab with Checklist Integration */}
                <div
                  className={`tab-pane ${activeTab === "duty-roster" ? "show active" : ""}`}
                  id="duty-roster"
                >
                  <div className="card">
                    <div className="card-body">
                      <DataTable
                        value={filteredDutyRoster}
                        emptyMessage="No duty roster entries found."
                        sortMode="multiple"
                        paginator
                        rows={10}
                        stripedRows
                        loading={loading}
                        header={renderHeader(
                          "Duty Roster",
                          "Add Duty Roster",
                          () => handleDutyRosterModalOpen(),
                        )}
                        tableStyle={{ minWidth: "50rem" }}
                        expandedRows={expandedRows}
                        onRowToggle={(e) => setExpandedRows(e.data)}
                        rowExpansionTemplate={dutyRosterExpansionTemplate}
                      >
                        <Column expander style={{ width: "3em" }} />
                        <Column
                          field="staffId"
                          header="Staff"
                          body={staffTemplate}
                          sortable
                        />
                        <Column
                          field="staffPosition"
                          header="Position"
                          sortable
                        />
                        <Column
                          field="masterWorkplanId"
                          header="Master Workplan"
                          body={dutyRosterMasterWorkplanTemplate}
                          sortable
                        />
                        <Column
                          field="startDate"
                          header="Start Date"
                          body={(rowData) =>
                            rowData.startDate
                              ? new Date(rowData.startDate).toLocaleDateString()
                              : "N/A"
                          }
                          sortable
                        />
                        <Column
                          field="endDate"
                          header="End Date"
                          body={(rowData) =>
                            rowData.endDate
                              ? new Date(rowData.endDate).toLocaleDateString()
                              : "N/A"
                          }
                          sortable
                        />
                        <Column
                          field="checklistStatus"
                          header="Checklist Progress"
                          body={checklistStatusTemplate}
                          sortable
                        />
                        <Column
                          field="todaysTasks"
                          header="Today's Tasks"
                          body={todaysTasksTemplate}
                          sortable
                        />
                        <Column
                          body={dutyRosterActionTemplate}
                          header="Actions"
                        />
                      </DataTable>
                    </div>
                  </div>
                </div>

                {/* NEW: Task Checklists Tab */}
                <div
                  className={`tab-pane ${activeTab === "task-checklists" ? "show active" : ""}`}
                  id="task-checklists"
                >
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">Task Checklists Overview</h5>
                        <div className="d-flex gap-2">
                          <select
                            className="form-select"
                            onChange={(e) => {
                              // Filter checklists by status
                              const status = e.target.value;
                              if (status) {
                                // Apply filter logic here
                              }
                            }}
                          >
                            <option value="">All Statuses</option>
                            <option value="active">Active</option>
                            <option value="completed">Completed</option>
                            <option value="expired">Expired</option>
                          </select>
                        </div>
                      </div>

                      <div className="row">
                        {checklists.map((checklist) => {
                          const roster = dutyRoster.find(
                            (r) => r._id === checklist.dutyRosterId,
                          );
                          return (
                            <div key={checklist._id} className="col-md-6 mb-3">
                              <div className="card h-100">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                  <h6 className="mb-0">
                                    {roster?.staffDetails?.fullName ||
                                      "Unknown Staff"}
                                  </h6>
                                  <span
                                    className={`badge ${
                                      checklist.status === "active"
                                        ? "bg-success"
                                        : checklist.status === "completed"
                                          ? "bg-info"
                                          : checklist.status === "expired"
                                            ? "bg-warning"
                                            : "bg-secondary"
                                    }`}
                                  >
                                    {checklist.status}
                                  </span>
                                </div>
                                <div className="card-body">
                                  <div className="row text-center mb-3">
                                    <div className="col-3">
                                      <h6 className="text-success mb-0">
                                        {checklist.summary?.completedTasks || 0}
                                      </h6>
                                      <small>Done</small>
                                    </div>
                                    <div className="col-3">
                                      <h6 className="text-warning mb-0">
                                        {checklist.summary?.pendingTasks || 0}
                                      </h6>
                                      <small>Pending</small>
                                    </div>
                                    <div className="col-3">
                                      <h6 className="text-danger mb-0">
                                        {checklist.summary?.missedTasks || 0}
                                      </h6>
                                      <small>Missed</small>
                                    </div>
                                    <div className="col-3">
                                      <h6 className="text-primary mb-0">
                                        {checklist.summary?.totalTasks || 0}
                                      </h6>
                                      <small>Total</small>
                                    </div>
                                  </div>

                                  <div
                                    className="progress mb-2"
                                    style={{ height: "15px" }}
                                  >
                                    <div
                                      className="progress-bar"
                                      role="progressbar"
                                      style={{
                                        width: `${checklist.summary?.completionPercentage || 0}%`,
                                      }}
                                    >
                                      {checklist.summary
                                        ?.completionPercentage || 0}
                                      %
                                    </div>
                                  </div>

                                  <div className="d-flex justify-content-between align-items-center">
                                    <small className="text-muted">
                                      {checklist.period ? (
                                        <>
                                          {new Date(
                                            checklist.period.startDate,
                                          ).toLocaleDateString()}{" "}
                                          -{" "}
                                          {new Date(
                                            checklist.period.endDate,
                                          ).toLocaleDateString()}
                                        </>
                                      ) : (
                                        "No period set"
                                      )}
                                    </small>
                                    <Button
                                      icon="ti ti-eye"
                                      className="p-button-sm p-button-outlined"
                                      onClick={() =>
                                        handleChecklistModalOpen(roster)
                                      }
                                      tooltip="View Details"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {checklists.length === 0 && (
                        <div className="text-center py-5">
                          <i
                            className="ti ti-clipboard-off"
                            style={{ fontSize: "48px", color: "#ccc" }}
                          ></i>
                          <h6 className="mt-3 text-muted">
                            No Task Checklists Found
                          </h6>
                          <p className="text-muted">
                            Checklists are automatically created when you add
                            duty rosters with master workplans that have child
                            tasks.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* DAILY CHECKLIST TAB */}
                <div
                  className={`tab-pane ${activeTab === "daily-checklist" ? "show active" : ""}`}
                  id="daily-checklist"
                >
                  <div className="card">
                    <div className="card-header">
                      <h5 className="mb-0">Daily Checklist Management</h5>
                      <small className="text-muted">
                        Manage daily facility monitoring checklists with
                        historical readings tracking
                      </small>
                    </div>
                    <div className="card-body">
                      <DailyChecklistTab />
                    </div>
                  </div>
                </div>

                {/* Calendar Tab */}
                <div
                  className={`tab-pane ${activeTab === "calendar" ? "show active" : ""}`}
                  id="calendar"
                >
                  <div className="card">
                    <div className="card-body p-0">
                      <DutyRosterCalendar dutyRoster={dutyRoster} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showMasterWorkplanModal && (
        <MasterWorkplanModal
          show={showMasterWorkplanModal}
          onClose={() => setShowMasterWorkplanModal(false)}
          onSuccess={handleMasterWorkplanSuccess}
          facilityId={selectedFacilityId}
          workplan={selectedMasterWorkplan}
        />
      )}

      {showChildWorkplanModal && (
        <ChildWorkplanModal
          show={showChildWorkplanModal}
          onClose={() => setShowChildWorkplanModal(false)}
          onSuccess={handleChildWorkplanSuccess}
          facilityId={selectedFacilityId}
          workplan={selectedChildWorkplan}
          masterWorkplans={masterWorkplans}
          selectedMasterWorkplanId={selectedMasterForChild}
        />
      )}

      {showDutyRosterModal && (
        <DutyRosterModal
          show={showDutyRosterModal}
          onClose={() => setShowDutyRosterModal(false)}
          onSuccess={handleDutyRosterSuccess}
          facilityId={selectedFacilityId}
          roster={selectedDutyRoster}
          staffUsers={staffUsers}
          masterWorkplans={masterWorkplans}
        />
      )}

      {showTaskCompletionModal && (
        <TaskCompletionModal
          show={showTaskCompletionModal}
          onClose={() => setShowTaskCompletionModal(false)}
          onSuccess={() => {
            fetchTaskCompletions();
            setShowTaskCompletionModal(false);
            toastify("Task completion recorded successfully", "success");
          }}
          facilityId={selectedFacilityId}
          childTask={selectedTaskForCompletion}
          staffUsers={staffUsers}
          onSubmit={submitTaskCompletion}
        />
      )}

      {/* NEW: Checklist Management Modal */}
      {showChecklistModal && (
        <ChecklistManagementModal
          show={showChecklistModal}
          onClose={() => setShowChecklistModal(false)}
          onSuccess={handleChecklistSuccess}
          facilityId={selectedFacilityId}
          roster={selectedRosterForChecklist}
          staffUsers={staffUsers}
        />
      )}
    </Layout>
  );
};

export default WorkplanManagement;
